// libraries
import {
  breakpointSizes,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { Moment } from 'moment-timezone'
import React, { useEffect } from 'react'
// other
import { useSelector } from 'react-redux'
import ActiveUntilTooltip from 'src/components/ActiveUntilTooltip/ActiveUntilTooltip'
import AmountInputField from 'src/components/AmountInputField/AmountInputField'
// components
import Box from 'src/components/Box/Box'
import CreditCardDropdown, {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
  creditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import InvoiceAmountTooltip from 'src/components/InvoiceAmountTooltip/InvoiceAmountTooltip'
import InvoiceDateTooltip from 'src/components/InvoiceDateTooltip/InvoiceDateTooltip'
import InvoiceNumberTooltip from 'src/components/InvoiceNumberTooltip/InvoiceNumberTooltip'
import RadioItems from 'src/components/RadioItems/RadioItems'
import RecurrenceAmountAndFrequencyLabel from 'src/components/RecurrenceAmountAndFrequencyLabel/RecurrenceAmountAndFrequencyLabel'
import RecurrenceEndingLabel from 'src/components/RecurrenceEndingLabel/RecurrenceEndingLabel'
import RecurrenceForm, {
  RecurrenceFormChangeHandlers,
  RecurrenceFormErrors,
  RecurrenceFormValues,
} from 'src/components/RecurrenceForm/RecurrenceForm'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import Trns, { translateSingleString } from 'src/localization/Trns'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import {
  selectNullSafeLocaleFromState,
  selectNullSafeTimezoneFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { FormProps } from 'src/types'
// style
import { css } from 'styled-components'

interface CreditCardAndAmountInputBoxProps {
  className?: string
  timezone?: string
  labelCreditCardsAsAccounts?: boolean
  creditCardOptions?: CreditCardOption[]
  creditCardIdProps?: FormProps<string>
  invoiceNumberProps?: FormProps<string | undefined>
  invoiceDateMomentProps?: FormProps<Moment | undefined>
  balanceLabel: React.ReactElement
  // amount available to spend on the virtual card
  balanceDollarsProps: FormProps<number | undefined> | undefined
  limitLabel?: React.ReactElement
  // current total limit amount assigned to the virtual card
  limitDollarsProps?: FormProps<number | undefined>
  recurrenceDollarsProps?: FormProps<number | undefined>
  unlimitedAmountProps?: {
    isUnlimited: boolean
    setIsUnlimited: (val: boolean) => void
  }
  maxValue: number
  validToMomentProps?: FormProps<Moment | undefined>
  recurrenceLabel?: React.ReactElement
  recurrenceValues: RecurrenceFormValues
  recurrenceErrors: RecurrenceFormErrors
  recurrenceChangeHandlers?: RecurrenceFormChangeHandlers
  baseRecurrenceValues?: Partial<RecurrenceFormValues>
  currencyProps: FormProps<ExtendCurrency>
  creditCardInfiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  isApproveRequestForm?: boolean
  isRequest?: boolean
  id?: string
  accountMessage?: React.ReactElement
  amountPlaceholder?: string
  optional?: boolean
  hideInputFieldsAndLock?: boolean
  isUpdateForm?: boolean
  hideTooltips?: boolean
  isBillPay?: boolean
}

const CreditCardAndAmountInputBox: React.FC<
  CreditCardAndAmountInputBoxProps
> = (props): React.ReactElement => {
  const timezone = useSelector(selectNullSafeTimezoneFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const org = useSelector(selectUserExplicitOrganizationListModelFromState)

  const budgetOptions = props.creditCardOptions
    ?.filter((cc) => cc.details?.creditCard.isBudget)
    .map((cc) => {
      if (cc.details?.creditCard.organizationId !== org?.id) {
        cc.disabled = true
      }
      return cc
    })

  const rcnOptions = props.creditCardOptions?.filter(
    (cc) => !cc.details?.creditCard.isBudget,
  )

  const autoselectedCreditCardId =
    props.creditCardIdProps?.value !== undefined
      ? props.creditCardIdProps.value
      : props.creditCardOptions?.length === 1
        ? props.creditCardOptions[0].value
        : undefined

  const disabledOptions = [
    ...(budgetOptions !== undefined ? budgetOptions : []),
    ...(rcnOptions !== undefined ? rcnOptions : []),
  ]
    .filter((cc) => !!cc.disabled)
    .map((cc) => cc.details?.creditCard.id)

  const selectedValues =
    autoselectedCreditCardId !== undefined &&
    !(disabledOptions.indexOf(autoselectedCreditCardId) > -1)
      ? new Set([autoselectedCreditCardId])
      : new Set<string>()
  const selectedOptions =
    props.creditCardIdProps?.value !== undefined &&
    creditCards[props.creditCardIdProps?.value]
      ? [
          creditCardOption(
            creditCards[props.creditCardIdProps?.value],
            props.hideInputFieldsAndLock,
          ),
        ]
      : props.creditCardOptions?.length === 1 &&
          creditCards[props.creditCardOptions[0].value]
        ? [creditCardOption(creditCards[props.creditCardOptions[0].value])]
        : undefined

  useEffect((): void => {
    if (
      selectedOptions &&
      selectedValues &&
      props.creditCardIdProps !== undefined
    ) {
      props.creditCardIdProps.onChange(selectedOptions[0].value)
    }
  }, [props.creditCardOptions])

  const showRecurrenceUpdates =
    props.baseRecurrenceValues !== undefined &&
    (props.recurrenceValues.recurrenceDollars !==
      props.baseRecurrenceValues.recurrenceDollars ||
      props.recurrenceValues.period !== props.baseRecurrenceValues.period ||
      props.recurrenceValues.interval !== props.baseRecurrenceValues.interval ||
      props.recurrenceValues.terminator !==
        props.baseRecurrenceValues.terminator ||
      (props.recurrenceValues.terminator === RecurrenceTerminator.Count &&
        props.recurrenceValues.terminator ===
          props.baseRecurrenceValues.terminator &&
        props.recurrenceValues.count !== props.baseRecurrenceValues.count) ||
      (props.recurrenceValues.terminator === RecurrenceTerminator.Date &&
        props.recurrenceValues.terminator ===
          props.baseRecurrenceValues.terminator &&
        props.recurrenceValues.untilMoment !== undefined &&
        props.recurrenceValues.untilMoment.unix() !==
          props.baseRecurrenceValues.untilMoment?.unix()) ||
      (props.recurrenceValues.period === RecurrencePeriod.Weekly &&
        props.recurrenceValues.period === props.baseRecurrenceValues.period &&
        props.recurrenceValues.byWeekDay !==
          props.baseRecurrenceValues.byWeekDay) ||
      (props.recurrenceValues.period === RecurrencePeriod.Monthly &&
        props.recurrenceValues.period === props.baseRecurrenceValues.period &&
        props.recurrenceValues.byMonthDay !==
          props.baseRecurrenceValues.byMonthDay) ||
      (props.recurrenceValues.period === RecurrencePeriod.Yearly &&
        props.recurrenceValues.period === props.baseRecurrenceValues.period &&
        props.recurrenceValues.byYearDay !==
          props.baseRecurrenceValues.byYearDay))

  return (
    <Box
      className={props.className}
      noPadding
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          & > *:first-child {
            margin-top: 0px;
          }

          padding: 36px 40px;
          ${getBreakpointOnly(breakpointSizes.XXSM)(css`
            padding: 36px 20px;
          `)}
        `}
      >
        {props.accountMessage ? (
          <div
            css={css`
              margin-bottom: 40px;
            `}
          >
            {props.accountMessage}
          </div>
        ) : (
          <>
            {props.creditCardIdProps !== undefined &&
              (budgetOptions !== undefined ||
                rcnOptions !== undefined ||
                props.hideInputFieldsAndLock) && (
                <CreditCardDropdown
                  id={props.id}
                  css={css`
                    margin-top: 24px;
                    z-index: 2;
                  `}
                  fieldLabel={
                    props.labelCreditCardsAsAccounts ? (
                      <Trns source={'Account'} />
                    ) : (
                      <Trns source={'Budget'} />
                    )
                  }
                  errors={props.creditCardIdProps.errors}
                  optionGroups={[
                    ...(budgetOptions !== undefined && budgetOptions.length > 0
                      ? [
                          {
                            label: <Trns source={'Budgets'} />,
                            options: budgetOptions,
                          },
                        ]
                      : []),
                    ...(rcnOptions !== undefined && rcnOptions.length > 0
                      ? [
                          {
                            label: <Trns source={'Accounts'} />,
                            options: rcnOptions,
                          },
                        ]
                      : []),
                  ]}
                  chooseOption={props.creditCardIdProps.onChange}
                  selectedValues={selectedValues}
                  isMulti={false}
                  placeholder={translateSingleString(
                    locale,
                    'Type or select from list',
                  )}
                  isStatic={props.creditCardIdProps.isStatic}
                  infiniteScrollProps={props.creditCardInfiniteScrollProps}
                  selectedOptions={selectedOptions}
                />
              )}
          </>
        )}
        {props.invoiceDateMomentProps && props.invoiceNumberProps && (
          <div
            css={css`
              margin-top: 24px;
              width: 100%;
              display: flex;
              justify-content: space-between;
              gap: 40px;
            `}
          >
            <TextInputField
              id={'invoiceNumberTextInputField'}
              css={css`
                width: 100%;
              `}
              fieldLabel={<Trns source={'Invoice Number'} />}
              showUpdates={props.invoiceNumberProps.showUpdates}
              baseValue={props.invoiceNumberProps.baseValue}
              value={props.invoiceNumberProps.value}
              onChange={props.invoiceNumberProps.onChange}
              errors={props.invoiceNumberProps.errors}
              tooltip={
                !props.hideTooltips ? (
                  <InvoiceNumberTooltip
                    openLeft={false}
                    css={css`
                      height: 18px;
                      margin-bottom: 6px;
                    `}
                  />
                ) : undefined
              }
              isStatic={props.hideInputFieldsAndLock}
              placeholder={translateSingleString(locale, 'e.g. 0451')}
              design={TextInputFieldDesigns.New}
              characterLimit={255}
              hideCharacterLimitCount
            />
            <SingleDateInputField
              id={'invoiceDateDateInputField'}
              css={css`
                width: 100%;
              `}
              fieldLabel={<Trns source={'Invoice Date'} />}
              allowAllDates
              tooltip={
                !props.hideTooltips ? (
                  <InvoiceDateTooltip
                    openLeft={false}
                    css={css`
                      height: 18px;
                      margin-bottom: 6px;
                    `}
                  />
                ) : undefined
              }
              isStatic={props.hideInputFieldsAndLock}
              showUpdates={props.invoiceDateMomentProps.showUpdates}
              baseValue={props.invoiceDateMomentProps.baseValue}
              errors={props.invoiceDateMomentProps.errors}
              dateMoment={props.invoiceDateMomentProps.value}
              onDateMomentChange={props.invoiceDateMomentProps.onChange}
            />
          </div>
        )}
        {!props.hideInputFieldsAndLock &&
          props.balanceDollarsProps &&
          props.unlimitedAmountProps === undefined && (
            <AmountInputField
              css={css`
                margin-top: 24px;
              `}
              fieldLabel={props.balanceLabel}
              showUpdates={props.balanceDollarsProps.showUpdates}
              baseValue={props.balanceDollarsProps.baseValue}
              value={props.balanceDollarsProps.value}
              onChange={props.balanceDollarsProps.onChange}
              errors={props.balanceDollarsProps.errors}
              maxValue={props.maxValue}
              currency={props.currencyProps.value}
              baseCurrency={props.currencyProps.baseValue}
              onCurrencyChange={props.currencyProps.onChange}
              isRequest={
                props.isRequest !== undefined ? props.isRequest : false
              }
              placeholder={props.amountPlaceholder}
              optional={!!props.optional}
              tooltip={
                !props.hideTooltips && props.isBillPay ? (
                  <InvoiceAmountTooltip
                    openLeft={false}
                    css={css`
                      height: 18px;
                      margin-bottom: 6px;
                    `}
                  />
                ) : undefined
              }
              static={props.balanceDollarsProps.isStatic}
            />
          )}
        {!props.hideInputFieldsAndLock &&
          props.limitDollarsProps &&
          props.unlimitedAmountProps === undefined && (
            <AmountInputField
              css={css`
                margin-top: 24px;
              `}
              fieldLabel={props.limitLabel}
              showUpdates={props.limitDollarsProps.showUpdates}
              baseValue={props.limitDollarsProps.baseValue}
              value={props.limitDollarsProps.value}
              onChange={props.limitDollarsProps.onChange}
              errors={props.limitDollarsProps.errors}
              maxValue={props.maxValue}
              currency={props.currencyProps.value}
              baseCurrency={props.currencyProps.baseValue}
              onCurrencyChange={props.currencyProps.onChange}
              isRequest={
                props.isRequest !== undefined ? props.isRequest : false
              }
              placeholder={props.amountPlaceholder}
              optional={!!props.optional}
              tooltip={
                !props.hideTooltips && props.isBillPay ? (
                  <InvoiceAmountTooltip
                    openLeft={false}
                    css={css`
                      height: 18px;
                      margin-bottom: 6px;
                    `}
                  />
                ) : undefined
              }
              static={props.limitDollarsProps.isStatic}
            />
          )}
        {props.hideInputFieldsAndLock &&
          props.balanceDollarsProps?.value !== undefined &&
          props.currencyProps.baseValue && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-top: 24px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {props.balanceLabel}
              </span>
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                <Crncy
                  currency={props.currencyProps.baseValue}
                  amount={props.balanceDollarsProps.value}
                />
              </span>
            </div>
          )}
        {props.hideInputFieldsAndLock &&
          props.limitDollarsProps?.value !== undefined &&
          props.currencyProps.baseValue && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-top: 24px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {props.limitLabel}
              </span>
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                <Crncy
                  currency={props.currencyProps.baseValue}
                  amount={props.limitDollarsProps.value}
                />
              </span>
            </div>
          )}
        {props.hideInputFieldsAndLock &&
          props.recurrenceDollarsProps?.value !== undefined &&
          props.currencyProps.baseValue && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-top: 24px;
              `}
            >
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  margin-bottom: 8px;
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {props.recurrenceLabel}
                {!props.hideTooltips && props.isBillPay && (
                  <InvoiceAmountTooltip
                    openLeft={false}
                    css={css`
                      height: 18px;
                      margin-bottom: 6px;
                    `}
                  />
                )}
              </span>
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                <Crncy
                  currency={props.currencyProps.baseValue}
                  amount={props.recurrenceDollarsProps.value}
                />
              </span>
            </div>
          )}
        {props.unlimitedAmountProps !== undefined &&
          props.balanceDollarsProps !== undefined && (
            <RadioItems
              css={css`
                margin-top: 24px;
              `}
              fieldLabel={props.balanceLabel}
              id={props.balanceLabel.props.source}
              noBorder
              staticOnHover
              centered
              items={[
                <AmountInputField
                  key={0}
                  showUpdates={props.balanceDollarsProps.showUpdates}
                  baseValue={props.balanceDollarsProps.baseValue}
                  value={props.balanceDollarsProps.value}
                  onChange={props.balanceDollarsProps.onChange}
                  errors={props.balanceDollarsProps.errors}
                  maxValue={props.maxValue}
                  currency={props.currencyProps.value}
                  baseCurrency={props.currencyProps.baseValue}
                  onCurrencyChange={props.currencyProps.onChange}
                  isRequest={
                    props.isRequest !== undefined ? props.isRequest : false
                  }
                  placeholder={props.amountPlaceholder}
                  optional={!!props.optional}
                />,
                <span key={1} className={'unlimited'}>
                  <Trns source={'Unlimited'} />
                </span>,
              ]}
              currentIndex={props.unlimitedAmountProps.isUnlimited ? 1 : 0}
              setIndex={(val: number): void => {
                props.unlimitedAmountProps?.setIsUnlimited(val !== 0)
              }}
            />
          )}
        {props.validToMomentProps !== undefined && (
          <SingleDateInputField
            id={'activeUntilDateInputField'}
            css={css`
              margin-top: 24px;
            `}
            fieldLabel={<Trns source={'Active Until'} />}
            tooltip={
              !props.hideTooltips ? (
                <ActiveUntilTooltip
                  openLeft={false}
                  css={css`
                    height: 18px;
                    margin-bottom: 6px;
                  `}
                />
              ) : undefined
            }
            isStatic={props.hideInputFieldsAndLock}
            showUpdates={props.validToMomentProps.showUpdates}
            baseValue={props.validToMomentProps.baseValue}
            errors={props.validToMomentProps.errors}
            dateMoment={props.validToMomentProps.value}
            onDateMomentChange={props.validToMomentProps.onChange}
            placeHolder={props.validToMomentProps.placeHolder}
          />
        )}
        {!props.hideInputFieldsAndLock &&
        props.recurrenceChangeHandlers !== undefined ? (
          <>
            {props.isUpdateForm &&
              props.recurrenceChangeHandlers?.onRecurrenceDollarsChange && (
                <AmountInputField
                  css={css`
                    margin-top: 48px;
                    margin-bottom: 0;
                  `}
                  fieldLabel={<Trns source={'Resets to'} />}
                  showUpdates={props.recurrenceDollarsProps?.showUpdates}
                  baseValue={
                    props.recurrenceDollarsProps?.baseValue ??
                    props.baseRecurrenceValues?.recurrenceDollars
                  }
                  value={
                    props.recurrenceDollarsProps?.value ??
                    props.recurrenceValues.recurrenceDollars
                  }
                  onChange={
                    props.recurrenceChangeHandlers?.onRecurrenceDollarsChange
                  }
                  errors={props.recurrenceErrors.recurrenceDollars}
                  maxValue={props.maxValue}
                  currency={props.currencyProps.value}
                  baseCurrency={props.currencyProps.baseValue}
                  onCurrencyChange={props.currencyProps.onChange}
                  isRequest={
                    props.isRequest !== undefined ? props.isRequest : false
                  }
                  placeholder={props.amountPlaceholder}
                  optional={!!props.optional}
                  testId='resets-to-amount-input'
                />
              )}
            <RecurrenceForm
              css={css`
                margin-top: ${props.isUpdateForm ? 20 : 32}px;
              `}
              recurrenceValues={props.recurrenceValues}
              recurrenceErrors={props.recurrenceErrors}
              recurrenceChangeHandlers={props.recurrenceChangeHandlers}
            />
          </>
        ) : (
          props.recurrenceChangeHandlers !== undefined &&
          props.currencyProps.value !== undefined &&
          props.recurrenceValues.period !== undefined &&
          props.recurrenceValues.interval !== undefined &&
          props.recurrenceValues.terminator !== undefined &&
          props.recurrenceValues.amountDollars !== undefined &&
          props.hideInputFieldsAndLock && (
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )}
                ${showRecurrenceUpdates &&
                css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              `}
            >
              <Trns
                source={
                  props.recurrenceValues.terminator ===
                  RecurrenceTerminator.Count
                    ? '${recurrenceAmountAndFrequencyLabel}. ${recurrenceEndingLabel}.'
                    : '${recurrenceAmountAndFrequencyLabel}, ${recurrenceEndingLabel}.'
                }
                expressions={{
                  recurrenceAmountAndFrequencyLabel: (
                    <RecurrenceAmountAndFrequencyLabel
                      period={props.recurrenceValues.period}
                      balance={
                        props.isUpdateForm
                          ? props.recurrenceValues.recurrenceDollars || 0
                          : props.balanceDollarsProps?.value || 0
                      }
                      interval={props.recurrenceValues.interval}
                      byWeekDay={props.recurrenceValues.byWeekDay}
                      byMonthDay={props.recurrenceValues.byMonthDay}
                      byYearDay={props.recurrenceValues.byYearDay}
                      timezone={props.timezone || timezone}
                      currency={props.currencyProps.value}
                    />
                  ),
                  recurrenceEndingLabel: (
                    <RecurrenceEndingLabel
                      terminator={props.recurrenceValues.terminator}
                      count={props.recurrenceValues.count}
                      untilMoment={props.recurrenceValues.untilMoment}
                    />
                  ),
                }}
              />
            </span>
          )
        )}
      </div>
      {props.recurrenceChangeHandlers !== undefined &&
        props.currencyProps.value !== undefined &&
        props.recurrenceValues.period !== undefined &&
        props.recurrenceValues.interval !== undefined &&
        props.recurrenceValues.terminator !== undefined &&
        props.recurrenceValues.amountDollars !== undefined &&
        !props.hideInputFieldsAndLock && (
          <div
            css={css`
              border-top: 1px solid ${styleConstants.colors.gray_light};
              background-color: ${styleConstants.colors.gray_lightest};
              border-bottom-left-radius: ${styleConstants.borderRadius.box};
              border-bottom-right-radius: ${styleConstants.borderRadius.box};
              display: flex;
              flex-direction: column;
              padding: 36px 40px;
              ${getBreakpointOnly(breakpointSizes.XXSM)(css`
                padding: 36px 20px;
              `)}
            `}
          >
            <span
              css={css`
                margin-bottom: 20px;
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.medium,
                )}

                ${showRecurrenceUpdates &&
                css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.medium,
                  )}
                `}
              `}
            >
              <Trns source={'Summary'} />
            </span>
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )}
                ${showRecurrenceUpdates &&
                css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              `}
            >
              <Trns
                source={
                  props.recurrenceValues.terminator ===
                  RecurrenceTerminator.Count
                    ? '${recurrenceAmountAndFrequencyLabel}. ${recurrenceEndingLabel}.'
                    : '${recurrenceAmountAndFrequencyLabel}, ${recurrenceEndingLabel}.'
                }
                expressions={{
                  recurrenceAmountAndFrequencyLabel: (
                    <RecurrenceAmountAndFrequencyLabel
                      period={props.recurrenceValues.period}
                      balance={
                        props.isUpdateForm
                          ? props.recurrenceValues.recurrenceDollars || 0
                          : props.balanceDollarsProps?.value || 0
                      }
                      interval={props.recurrenceValues.interval}
                      byWeekDay={props.recurrenceValues.byWeekDay}
                      byMonthDay={props.recurrenceValues.byMonthDay}
                      byYearDay={props.recurrenceValues.byYearDay}
                      timezone={props.timezone || timezone}
                      currency={props.currencyProps.value}
                    />
                  ),
                  recurrenceEndingLabel: (
                    <RecurrenceEndingLabel
                      terminator={props.recurrenceValues.terminator}
                      count={props.recurrenceValues.count}
                      untilMoment={props.recurrenceValues.untilMoment}
                    />
                  ),
                }}
              />
            </span>
          </div>
        )}
    </Box>
  )
}

export default CreditCardAndAmountInputBox
